import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { HelianthusArbiter } from '@amzn/helianthus-arbiter-client';
import { CredentialExchangeError, createMidwayCredentialProvider } from '../api/auth';
import { getSettings, type AppSettings } from '../api/settings';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';

interface ArbiterClientContextValue {
  client: HelianthusArbiter;
  settings: AppSettings;
}

const ArbiterClientContext = createContext<ArbiterClientContextValue | null>(null);

interface ArbiterClientProviderProps {
  children: ReactNode;
}

/** Which step failed decides the recovery on offer: clearing the token cannot fix a bad settings.json. */
type BootFailure = { message: string; variant: 'api-error' | 'auth-expired' };

export function ArbiterClientProvider({ children }: ArbiterClientProviderProps): ReactNode {
  const [value, setValue] = useState<ArbiterClientContextValue | null>(null);
  const [failure, setFailure] = useState<BootFailure | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function init(): Promise<void> {
      const settings = await getSettings();
      if (cancelled) return; // StrictMode double-run: only the surviving effect may sign in.

      const credentials = createMidwayCredentialProvider();
      await credentials(); // Sign in before rendering; a Midway redirect leaves from this screen.
      const arbiterClient = new HelianthusArbiter({
        region: settings.region,
        endpoint: settings.apiEndpoint,
        credentials,
      });
      if (!cancelled) {
        setValue({ client: arbiterClient, settings });
      }
    }

    init().catch((err) => {
      if (cancelled) return;
      setFailure({
        message: err instanceof Error ? err.message : String(err),
        // Only a token STS rejected is worth clearing; every other failure survives a fresh one.
        variant: err instanceof CredentialExchangeError && err.tokenRejected ? 'auth-expired' : 'api-error',
      });
    });

    return () => { cancelled = true; };
  }, []);

  // A rejected token gets ErrorState's auth-expired variant, whose action clears the cached token before
  // reloading. Everything else (settings, STS access-denied, an outage) gets a plain retry, because clearing
  // a good token and minting another one would hit the same failure.
  if (failure) {
    return (
      <ErrorState
        variant={failure.variant}
        title="Failed to initialize client"
        message={failure.message}
        onRetry={failure.variant === 'api-error' ? () => window.location.reload() : undefined}
      />
    );
  }

  if (!value) {
    return <LoadingState label="Signing in with Midway..." />;
  }

  return (
    <ArbiterClientContext.Provider value={value}>
      {children}
    </ArbiterClientContext.Provider>
  );
}

function useArbiterContext(hookName: string): ArbiterClientContextValue {
  const ctx = useContext(ArbiterClientContext);
  if (!ctx) {
    throw new Error(`${hookName} must be used within ArbiterClientProvider`);
  }
  return ctx;
}

export function useArbiterClient(): HelianthusArbiter {
  return useArbiterContext('useArbiterClient').client;
}

/** Stage settings loaded from settings.json (apiEndpoint, roleArn, stage, ...). */
export function useSettings(): AppSettings {
  return useArbiterContext('useSettings').settings;
}
